import { debounce } from 'lodash-es';
import { normalizeSrc } from '@uw/utils';
import { RuleParams } from '../../../types/remediation-rule';
import { ImageAltConfig } from '../../config/config';
import {
  getImageSource,
  getPageImagesMajorityGroup,
  isThirdPartyTechImage,
} from './image-utils';
import {
  IMG_ALTS_DEBOUNCE_MAX_TIME,
  IMG_ALTS_DEBOUNCE_TIME,
  SHOW_IN_WIDGET_REMEDIATION_LIST,
} from './constants';
import { getMissingAlts } from '../../api/missing-alt';
import {
  imageInfoToJson,
  prepareMissingAltData,
} from './prepare-missing-alt-data';
import { processImages } from './utils/process-images';
import { shouldBeDecorative } from './should-be-decorative';

const imagesQueue: HTMLElement[] = [];

const clearImagesQueue = (): void => {
  imagesQueue.length = 0;
};

export const imageAltRule = (elements: HTMLElement[]): void => {
  const { state } = ImageAltConfig;
  if (state === 'ALTS_OFF') {
    return;
  }

  const imgMajorityGroup = getPageImagesMajorityGroup(
    elements as HTMLImageElement[],
  );

  const imagesForBE: HTMLImageElement[] = [];
  const imagesNotForBE: HTMLImageElement[] = [];

  for (const image of elements) {
    const imageEl = image as HTMLImageElement;
    const isThirdPartyImg = isThirdPartyTechImage(imageEl, imgMajorityGroup);
    const decorativeReason = shouldBeDecorative(imageEl);
    const isImageHasDecorativeReasonNotForBE =
      decorativeReason &&
      !SHOW_IN_WIDGET_REMEDIATION_LIST.includes(decorativeReason);
    const isImageNotForBE =
      isImageHasDecorativeReasonNotForBE || isThirdPartyImg;

    if (isImageNotForBE) {
      imagesNotForBE.push(imageEl);
    } else {
      imagesForBE.push(imageEl);
    }
  }

  try {
    const imagesList = prepareMissingAltData(imagesForBE);
    const jsonImagesData = imageInfoToJson(imagesList);

    for (const json of jsonImagesData) {
      getMissingAlts(json).then(({ missingAlts }) => {
        const imagesToProcess = imagesForBE.filter((image) =>
          missingAlts.find(
            (item) =>
              normalizeSrc(item.src) === normalizeSrc(getImageSource(image)),
          ),
        );
        processImages(imagesToProcess, missingAlts);
      });
    }
  } catch (error) {
    console.error(error);
  }

  processImages(imagesNotForBE as HTMLImageElement[]);
  clearImagesQueue();
};

const debouncedAltRule = debounce(
  (elements): void => imageAltRule(elements),
  IMG_ALTS_DEBOUNCE_TIME,
  {
    maxWait: IMG_ALTS_DEBOUNCE_MAX_TIME,
    leading: true,
    trailing: true,
  },
);

export const runImgAltRule = ({ context: { elements } }: RuleParams): void => {
  imagesQueue.push(...elements);
  debouncedAltRule(imagesQueue);
};
